// Browser side of Web Push alerts: the service worker (public/sw.js), the
// PushManager subscription and this device's push registration on the server.
import { api, publicApi, session, unwrap } from "../api";
import { base64UrlToBytes, webPushRegistration } from "./notifications";

export type WebPushStatus = "unsupported" | "disabled" | "denied" | "off" | "on";

/** Sign-out waits at most this long for push cleanup. */
const FORGET_TIMEOUT_MS = 3_000;
const LOCK = "tentwenty-web-push";

export function webPushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

/**
 * Runs subscription changes one at a time across tabs, so an enable or sync
 * cannot interleave with sign-out's unsubscribe.
 */
function exclusive<T>(work: () => Promise<T>): Promise<T> {
  return typeof navigator.locks === "undefined" ? work() : (navigator.locks.request(LOCK, work) as Promise<T>);
}

async function subscription(): Promise<PushSubscription | null> {
  const registration = await navigator.serviceWorker.getRegistration("/");
  return (await registration?.pushManager.getSubscription()) ?? null;
}

function sameKey(subscription: PushSubscription, publicKey: string): boolean {
  const current = subscription.options.applicationServerKey;
  if (!current) return false;
  const expected = base64UrlToBytes(publicKey);
  const actual = new Uint8Array(current);
  return actual.length === expected.length && actual.every((byte, i) => byte === expected[i]);
}

/**
 * Whether this browser gets alerts; `disabled` means the server has no VAPID
 * key. A subscription made for another key (rotated server keys) counts as off.
 */
export async function webPushStatus(publicKey: string | null): Promise<WebPushStatus> {
  if (!webPushSupported()) return "unsupported";
  if (!publicKey) return "disabled";
  if (Notification.permission === "denied") return "denied";
  const current = Notification.permission === "granted" ? await subscription() : null;
  return current && sameKey(current, publicKey) ? "on" : "off";
}

async function savePush(deviceId: string, push: ReturnType<typeof webPushRegistration>) {
  unwrap(await api.PATCH("/me/devices/{deviceId}", { params: { path: { deviceId } }, body: { push } }));
}

/**
 * Registers `current` for `deviceId`, unless the session changed meanwhile
 * (signed out or switched account): then the subscription is dropped instead,
 * so the previous account's alerts never reach it.
 */
async function register(deviceId: string, current: PushSubscription) {
  const push = webPushRegistration(current.toJSON());
  if (!push) throw new Error("This browser returned an unusable push subscription.");
  if (session.current()?.deviceId !== deviceId) {
    await current.unsubscribe();
    return;
  }
  await savePush(deviceId, push);
}

/** Asks for permission, subscribes and registers the subscription for this device. */
export async function enableWebPush(publicKey: string): Promise<void> {
  const deviceId = session.current()?.deviceId;
  if (!deviceId) return;
  if ((await Notification.requestPermission()) !== "granted")
    throw new Error("Notifications are blocked for this site. Allow them in your browser's site settings.");
  await exclusive(async () => {
    await navigator.serviceWorker.register("/sw.js");
    const registration = await navigator.serviceWorker.ready;
    let current = await registration.pushManager.getSubscription();
    // A subscription made for another server key cannot receive our pushes.
    if (current && !sameKey(current, publicKey)) {
      await current.unsubscribe();
      current = null;
    }
    current ??= await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToBytes(publicKey),
    });
    await register(deviceId, current);
  });
}

/** Unsubscribes this browser and removes the device's push registration. */
export async function disableWebPush(): Promise<void> {
  const deviceId = session.current()?.deviceId;
  await exclusive(async () => {
    await (await subscription())?.unsubscribe();
    if (deviceId) await savePush(deviceId, null);
  });
}

/**
 * Drops this browser's subscription before signing out, so neither a session
 * whose logout failed offline nor already queued pushes show the previous
 * account's alerts to the next person. Bounded and best effort: sign-out goes
 * ahead after FORGET_TIMEOUT_MS, and an enable still running then drops its
 * subscription when it sees the session gone.
 */
export async function forgetWebPush(): Promise<void> {
  if (!webPushSupported()) return;
  const deviceId = session.current()?.deviceId;
  const forget = exclusive(async () => {
    const current = await subscription();
    if (!current) return;
    await current.unsubscribe();
    if (deviceId) await savePush(deviceId, null);
  }).catch(() => {
    // Offline or already signed out: the unsubscribe alone stops delivery.
  });
  await Promise.race([forget, new Promise((resolve) => setTimeout(resolve, FORGET_TIMEOUT_MS))]);
}

/**
 * Re-sends an existing subscription after sign-in or a browser rotation, so
 * the current device session receives alerts. A subscription for another
 * server key is dropped (and its registration cleared). Errors are ignored.
 */
export async function syncWebPush(): Promise<void> {
  try {
    const deviceId = session.current()?.deviceId;
    if (!deviceId || !webPushSupported() || Notification.permission !== "granted") return;
    const { webpush_public_key: publicKey } = unwrap(await publicApi.GET("/push/config"));
    await exclusive(async () => {
      const current = await subscription();
      if (!current) return;
      if (publicKey && sameKey(current, publicKey)) return register(deviceId, current);
      await current.unsubscribe();
      if (session.current()?.deviceId === deviceId) await savePush(deviceId, null);
    });
  } catch {
    // Best effort: the alerts page shows the real state.
  }
}
